import { Component, HostListener, OnInit, Renderer2 } from '@angular/core';
import { Platform, ModalController } from '@ionic/angular';
import { NetworkConnectivityComponent } from './components/network-connectivity/network-connectivity.component';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from './services/API/api.service';
import { GlobalService } from 'src/app/services/global/global.service';
import { TestDriveFormComponent } from './components/test-drive-form/test-drive-form.component';
import { EventService } from './services/Events/events.service';

declare global {
  interface Window {
    visualizer: boolean;
  }
}
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  orientationError = false;
  browserNotSupported = false;
  orientationErrorType: any;
  private mouseHandler?: (e: MouseEvent) => void;
  private interactionHandler?: () => void;

  constructor(
    private platform: Platform,
    private modalController: ModalController,
    private translate: TranslateService,
    private apiService: ApiService,
    private globalService: GlobalService,
    private renderer: Renderer2,
    private events: EventService
  ) {
    this.initializeLangApp();
    // to add class to the body for default idea
    this.renderer.addClass(document.body, `idea-${this.globalService.idea}`);
    if (this.platform.is('cordova')) {
      alert('CORDOVA');
    }
    this.orientationCheck();
    const browser = this.getBrowserInfo();
    this.browserControl(browser);
    window.addEventListener(
      'load',
      () => {
        if (navigator.onLine) {
          this.updateConnectionStatus('Online Load', true);
        } else {
          this.updateConnectionStatus('Offline', false);
        }
      },
      false
    );
    window.addEventListener(
      'online',
      () => {
        this.updateConnectionStatus('Online', true);
      },
      false
    );

    window.addEventListener(
      'offline',
      () => {
        this.updateConnectionStatus('Offline', false);
      },
      false
    );
  }

  ngOnInit(): void {
    // setupExitIntent after 3 secs - so that on hover down to page it doesn't show TDF
    setTimeout(() => {
      if (this.isDesktop()) {
        // Wait for first user interaction before attaching listeners
        this.interactionHandler = () => {
          console.log('first user interaction');
          this.setupExitIntent();
          window.removeEventListener('click', this.interactionHandler!);
          window.removeEventListener('keydown', this.interactionHandler!);
          window.removeEventListener('mousemove', this.interactionHandler!);
        };
        window.addEventListener('click', this.interactionHandler);
        window.addEventListener('keydown', this.interactionHandler);
        window.addEventListener('mousemove', this.interactionHandler);
      }
    }, 3000);
  }

  ngOnDestroy() {
    if (this.interactionHandler) {
      window.removeEventListener('click', this.interactionHandler);
      window.removeEventListener('keydown', this.interactionHandler);
      window.removeEventListener('mousemove', this.interactionHandler);
    }
    if (this.mouseHandler)
      document.removeEventListener('mouseout', this.mouseHandler);
  }

  private setupExitIntent() {
    // Detect mouse moving out at top
    this.mouseHandler = (e: MouseEvent) => {
      if (!this.globalService.shownTDF && e.clientY < 10) {
        console.log('///onExitTestDriveForm first time');
        this.onExitTestDriveForm();
      }
    };
    document.addEventListener('mouseout', this.mouseHandler);
  }

  private isDesktop() {
    return !('ontouchstart' in window) && window.innerWidth >= 1024;
  }

  async onExitTestDriveForm() {
    this.globalService.shownTDF = true;
    const modal = await this.modalController.create({
      component: TestDriveFormComponent,
      componentProps: {
        contentData: {
          header: 'CONFIGURATOR_HEADER_MSG.HEADER',
          msgContent: 'CONFIGURATOR_HEADER_MSG.CONTENT',
        },
        type: 'old',
        from: 'exit intent',
      },
      backdropDismiss: false,
      cssClass: 'one3d-test-drive-modal ' + ' auto-height',
      animated: false,
    });
    await modal.present();
    this.apiService.logGAEvent(
      'uaevent',
      'Content',
      'Redirection::InternalTestDrive',
      'test drive exit intent'
    );
  }

  initializeLangApp() {
    this.translate.setDefaultLang('en');
  }

  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  onResizeOrOrientationChange() {
    this.orientationCheck();
  }

  async updateConnectionStatus(msg: string, connected: boolean) {
    if (msg === 'Offline' && !connected) {
      const modal = await this.modalController.create({
        component: NetworkConnectivityComponent,
        cssClass: 'one-3d-full-page-modal',
        backdropDismiss: false,
        animated: false,
      });
      return await modal.present();
    } else if (msg === 'Online Load') {
      return;
    } else {
      this.globalService.dismissModal();
    }
  }
  // Detect Operating Systems of Mobile Devices
  getMobileSystem() {
    var userAgent = navigator.userAgent || navigator.vendor;

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
      return 'Windows Phone';
    }
    if (/android/i.test(userAgent)) {
      return 'Android';
    }
    // iOS detection for iPhones and iPods
    if (/iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }
    // iOS detection for iPads
    if (/iPad|Safari|Apple/i.test(userAgent)) {
      return 'iPad';
    }

    return 'unknown';
  }
  // Apply Orientation change
  one3d_orientation_block() {
    let device = this.getMobileSystem(); // Windows Phone,Android, iOS, iPad, unknown

    // Condition to apply orientation as per devices - iPad, Android, iOS
    switch (device) {
      case 'iPad':
        if (window.matchMedia('(orientation: portrait)').matches) {
          this.openModalForOrientation('ipad');
          this.orientationErrorType = 'ipad';
        } else {
          this.closeOrientationModal();
        }
        break;
      case 'Android':
        if (window.matchMedia('(orientation: portrait)').matches) {
          this.closeOrientationModal();
        } else {
          this.openModalForOrientation('mobile');
          this.orientationErrorType = 'mobile';
        }
        break;
      case 'iOS':
        if (window.matchMedia('(orientation: portrait)').matches) {
          this.closeOrientationModal();
        } else {
          this.openModalForOrientation('mobile');
          this.orientationErrorType = 'mobile';
        }
        break;
      default:
        this.closeOrientationModal();
        break;
    }
  }

  orientationCheck() {
    this.one3d_orientation_block();
    window.addEventListener(
      'orientationchange',
      () => {
        setTimeout(() => {
          this.one3d_orientation_block();
        }, 100);
      },
      false
    );
  }
  async openModalForOrientation(orientation) {
    this.orientationError = true;
  }
  closeOrientationModal() {
    this.orientationError = false;
  }

  checkBrowser() {
    const ua = navigator.userAgent;
    let tem;
    let M =
      ua.match(
        /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i
      ) || [];
    if (/trident/i.test(M[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
      return { name: 'IE', version: tem[1] || '' };
    }
    if (M[1] === 'Chrome') {
      tem = ua.match(/\bOPR|Edge\/(\d+)/);
      if (tem != null) {
        return { name: 'Opera', version: tem[1] };
      }
    }
    M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
    if ((tem = ua.match(/version\/(\d+)/i)) != null) {
      M.splice(1, 1, tem[1]);
    }
    return {
      name: M[0],
      version: M[1],
    };
  }
  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    let browserName;
    let browserVersion;

    // Detect Chrome
    if (userAgent.match(/Chrome\/(\S+)/)) {
      browserName = 'Chrome';
      browserVersion = userAgent.match(/Chrome\/(\S+)/)[1];
    }
    // Detect Firefox
    else if (userAgent.match(/Firefox\/(\S+)/)) {
      browserName = 'Firefox';
      browserVersion = userAgent.match(/Firefox\/(\S+)/)[1];
    }
    // Detect Safari
    else if (userAgent.match(/Version\/(\S+).*Safari/)) {
      browserName = 'Safari';
      browserVersion = userAgent.match(/Version\/(\S+).*Safari/)[1];
    }
    // Detect Edge
    else if (userAgent.match(/Edg\/(\S+)/)) {
      browserName = 'Edge';
      browserVersion = userAgent.match(/Edg\/(\S+)/)[1];
    }
    // Detect Internet Explorer
    else if (userAgent.match(/MSIE (\S+);/)) {
      browserName = 'Internet Explorer';
      browserVersion = userAgent.match(/MSIE (\S+);/)[1];
    }
    // Detect Opera
    else if (
      userAgent.match(/Opera\/.*?Version\/(\S+)/) ||
      userAgent.match(/Opera\/(\S+)/)
    ) {
      browserName = 'Opera';
      browserVersion = userAgent.match(
        /(?:Opera\/.*?Version\/(\S+))|(?:Opera\/(\S+))/
      )[1];
    } else {
      // For other browsers, you might need to handle them differently
      browserName = 'Other';
      browserVersion = 'N/A';
    }
    if (browserVersion != 'N/A') {
      let versionArray = browserVersion.split('.');
      browserVersion = Number(versionArray[0]);
    }
    return { name: browserName, version: browserVersion };
  }

  browserControl(browser) {
    if (
      browser.name === 'Internet Explorer' ||
      (browser.name === 'Safari' && browser.version < 14) ||
      (browser.name === 'Chrome' && browser.version < 86) ||
      (browser.name === 'Firefox' && browser.version < 90) ||
      (browser.name === 'Edge' && browser.version < 79) ||
      browser.name === 'Opera'
      //edge version and opera version need to be decided from 3d team
    ) {
      this.browserNotSupported = true;
      this.apiService.logGAEvent(
        'uaevent',
        'Content',
        'NotSupportedBrowser::Detail',
        navigator.userAgent
      );
      this.apiService.logGAEvent(
        'uaevent',
        'Content',
        'NotSupportedBrowser::Object',
        JSON.stringify(browser)
      );
    }
  }
}
